import { CommonModule } from '@angular/common';
import { Component, DestroyRef, inject, Input, OnChanges, OnInit, Signal, SimpleChanges } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormsModule } from '@angular/forms';
import { PremiumActivity } from '@lx/shared/data-access/school';
import { Amount } from '@lx/shared/data-access/types';
import { GoogleAnalyticsService } from '@lx/shared/google-analytics';
import { ButtonComponent } from '@lx/shared/ui/button';
import { LoadingService } from '@lx/shared/ui/loading-spinner';
import { PricePipe } from '@lx/shared/ui/pipes';
import { deepClone } from '@lx/shared/utils';
import { combineLatest, Observable, take } from 'rxjs';
import { GoogleAnalyticsEventEnum } from '../../google-analytics';
import { SelectedPremiumActivitiesPipe } from '../../pipes/selected-premium-activities/selected-premium-activities.pipe';
import { QTLiteStoreSignalsAdapterService } from '../../services/qt-lite-store-signals-adapter.service';
import { QuoteOutputProduct, QuoteStore, QuoteToolLiteStore, SharedDataStore } from '../../store';
import { PREMIUM_ACTIVITIES_FALLBACK_IMAGE } from '../../types';
import { SchoolLocation } from '@lx/quote-tool/lite/shared/types';
import { BasePremiumActivity } from '../../types/premium-activity.type';
import { CardWithCounterComponent } from '../card-with-counter/card-with-counter.component';
import { CardContainerComponent, ImageComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { LiteButtonComponent, MessageComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { SidePanelGalleryComponent } from '@lx/quote-tool/lite/shared/ui/components';
import { ExtendedPremiumActivity } from './extended-premium-activity.type';
import { OperationKey } from '../../constants';
import { SidePanelService } from '@lx/quote-tool/lite/shared/services';

@Component({
  standalone: true,
  selector: 'lx-premium-activities-with-options',
  imports: [
    CommonModule,
    CardContainerComponent,
    ButtonComponent,
    SidePanelGalleryComponent,
    CardWithCounterComponent,
    LiteButtonComponent,
    FormsModule,
    PricePipe,
    SelectedPremiumActivitiesPipe,
    ImageComponent,
    MessageComponent,
  ],
  templateUrl: './premium-activities-with-options.component.html',
  styleUrls: ['./premium-activities-with-options.component.scss'],
})
export class PremiumActivitiesWithOptionsComponent implements OnChanges, OnInit {
  @Input() premiumActivities: PremiumActivity[] | undefined;

  images: string[] = [];
  premiumActivitiesDialogVisible = false;
  schoolLocation$: Observable<SchoolLocation> | undefined;
  optionsVisible = false;
  totalPrice: Amount[] = [];
  selectedPremiumActivities: BasePremiumActivity[] = [];
  maxWeeksAchieved = false;
  selectedDuration: number | undefined;
  currency: Signal<string | undefined>;

  extendedPremiumActivities: ExtendedPremiumActivity[] = [];

  protected loading: Signal<boolean | undefined>;

  protected readonly PREMIUM_ACTIVITIES_FALLBACK_IMAGE = PREMIUM_ACTIVITIES_FALLBACK_IMAGE;

  // The suggested premium activity coming from extras-summary endpoint and
  // saved on SharedDataStore
  private suggestedPremiumActivityCode: string | undefined;

  private readonly quoteToolLiteStore = inject(QuoteToolLiteStore);
  private readonly qtLiteStoreSignalsAdapterService = inject(QTLiteStoreSignalsAdapterService);
  private readonly quoteStore = inject(QuoteStore);
  private readonly sharedDataStore = inject(SharedDataStore);
  private readonly sidePanelService = inject(SidePanelService);
  private readonly loadingService = inject(LoadingService);
  private readonly destroyRef = inject(DestroyRef);
  private readonly googleAnalyticsService = inject(GoogleAnalyticsService);

  ngOnInit(): void {
    this.schoolLocation$ = this.quoteToolLiteStore.selectedSchoolLocation$;

    this.sharedDataStore.suggestedPremiumActivityCode$
      .pipe(takeUntilDestroyed(this.destroyRef))
      .subscribe((suggestedPremiumActivityCode: string) => (this.suggestedPremiumActivityCode = suggestedPremiumActivityCode));

    this.currency = this.qtLiteStoreSignalsAdapterService.selectedCurrency;
    this.loading = this.loadingService.areAllOperationsLoading([OperationKey.prices, OperationKey.extras]);
  }

  ngOnChanges({ premiumActivities }: SimpleChanges): void {
    if (premiumActivities?.currentValue) {
      this.quoteToolLiteStore.selectedDuration$
        .pipe(take(1), takeUntilDestroyed(this.destroyRef))
        .subscribe((selectedDuration) => (this.selectedDuration = selectedDuration));

      this.images = this.getImages();

      // Clean previous selected premium activities
      this.selectedPremiumActivities = [];

      this.initializeExtendedPremiumActivities();

      combineLatest([this.quoteToolLiteStore.selectedPremiumActivities$, this.quoteStore.premiumActivities$])
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe(([selectedPremiumActivities, quotedPremiumActivities]: [BasePremiumActivity[], QuoteOutputProduct[]]) => {
          // Hydrate the extended premium activities with the prices and discounts from the quote store
          this.hydratePremiumActivitiesFromQuotedPremiumActivities(selectedPremiumActivities, quotedPremiumActivities);

          // Calculate total price of selected premium activities
          this.calculateTotalPriceForSelectedPremiumActivities(quotedPremiumActivities);
        });

      this.quoteToolLiteStore.selectedPremiumActivities$
        .pipe(takeUntilDestroyed(this.destroyRef))
        .subscribe((selectedPremiumActivities: BasePremiumActivity[]) => {
          if (selectedPremiumActivities?.length) {
            this.optionsVisible = true;
            this.selectedPremiumActivities = selectedPremiumActivities;
          }
          this.setMaxWeeksAchieved();
        });
    }
  }

  onWeeksChange(weeks: number, premiumActivityCode: string, premiumActivityName: string) {
    const existingPremiumActivity = this.selectedPremiumActivities.find((activity) => activity.code === premiumActivityCode);
    if (existingPremiumActivity) {
      existingPremiumActivity.units = weeks;
      existingPremiumActivity.name = premiumActivityName;
    } else {
      this.selectedPremiumActivities.push({ code: premiumActivityCode, units: weeks, name: premiumActivityName });
    }
    this.selectedPremiumActivities = this.selectedPremiumActivities.filter((activity) => !!activity.units);
    this.quoteToolLiteStore.updateState({ selectedPremiumActivities: this.selectedPremiumActivities });
    this.googleAnalyticsService.trackClickEvent(GoogleAnalyticsEventEnum.QuotePremiumActivitiesSelect);
  }

  openOptions() {
    this.selectSuggestedPremiumActivity();
    this.optionsVisible = true;
  }

  onLearnMoreClick() {
    this.premiumActivitiesDialogVisible = true;
  }

  hidePremiumActivitiesDialog() {
    this.sidePanelService.hidePanel(() => (this.premiumActivitiesDialogVisible = false));
  }

  private selectSuggestedPremiumActivity(): void {
    this.selectedPremiumActivities.push({
      code: this.suggestedPremiumActivityCode,
      units: 1,
    });
    this.quoteToolLiteStore.updateState({ selectedPremiumActivities: this.selectedPremiumActivities });
  }

  private getImages(): string[] {
    return this.premiumActivities?.reduce((acc, premiumActivity) => {
      acc.push(premiumActivity.thumbnailSmall);

      return acc;
    }, []);
  }

  private setMaxWeeksAchieved() {
    const sumOfTheWeeksSelected = this.selectedPremiumActivities.reduce((acc, activity) => acc + activity.units, 0);
    this.maxWeeksAchieved = sumOfTheWeeksSelected >= this.selectedDuration;
  }

  /**
   * Initialize the extended premium activities with the information from the premium activities
   */
  private initializeExtendedPremiumActivities() {
    this.extendedPremiumActivities = this.premiumActivities.map((premiumActivity: PremiumActivity) => ({
      ...deepClone(premiumActivity),
      price: premiumActivity.multiCurrencyPrice,
      endPrice: premiumActivity.multiCurrencyPrice,
      units: 0,
    }));
  }

  /**
   * Hydrate the extended premium activities with the prices and discounts from the quote store
   */
  private hydratePremiumActivitiesFromQuotedPremiumActivities(selectedPremiumActivities: BasePremiumActivity[], quotedPremiumActivities: QuoteOutputProduct[]) {
    quotedPremiumActivities?.forEach((quotedPremiumActivity: QuoteOutputProduct) => {
      const extendedPremiumActivity = this.extendedPremiumActivities.find(({ code }) => code === quotedPremiumActivity.code);
      if (extendedPremiumActivity) {
        const { price, endPrice, discountTotalAsPercentage } = quotedPremiumActivity;
        extendedPremiumActivity.price = price;
        extendedPremiumActivity.endPrice = endPrice;
        extendedPremiumActivity.discountTotalAsPercentage = discountTotalAsPercentage;
        extendedPremiumActivity.units =
          selectedPremiumActivities?.find((activity: BasePremiumActivity) => activity.code === quotedPremiumActivity.code)?.units || 0;
      }
    });
  }

  private calculateTotalPriceForSelectedPremiumActivities(quotedPremiumActivities: QuoteOutputProduct[]) {
    const totalPrice: Amount[] = [];
    quotedPremiumActivities?.forEach(({ endPrice }: QuoteOutputProduct) => {
      endPrice.forEach((price: Amount) => {
        const priceInTotalPrices: Amount = totalPrice.find((p) => p.currency === price.currency);
        if (!priceInTotalPrices) {
          totalPrice.push(deepClone(price));
        } else {
          priceInTotalPrices.value += price.value;
        }
      });
    });
    this.totalPrice = [...totalPrice];
  }
}
